<template>
  <div class="buyer-layout">
    <!-- NAVBAR -->
    <MainNavbar/>

    <!-- ENHANCED SEARCH BAR -->
    <div class="search-container">
      <div class="search-bar">
        <div class="search-input-wrapper">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input 
            type="text" 
            v-model="searchQuery"
            placeholder="Search products by title or brand..."
            @keyup.enter="performSearch"
            @input="handleSearchInput"
          />
          <button class="clear-search" @click="clearSearch" v-if="searchQuery">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <button class="search-btn" @click="performSearch">
          <i class="fa-solid fa-search"></i>
          <span>Search</span>
        </button>
      </div>
      
      <!-- SEARCH TIPS -->
      <div class="search-tips" v-if="searchQuery && searchResults.length > 0">
        <p><i class="fa-solid fa-lightbulb"></i> Found {{ searchResults.length }} results for "{{ searchQuery }}"</p>
      </div>
      
      <!-- QUICK FILTERS -->
      <div class="quick-filters">
        <button 
          class="quick-filter" 
          v-for="brand in popularBrands" 
          :key="brand"
          @click="searchByBrand(brand)"
        >
          <i class="fa-solid fa-tag"></i>
          {{ brand }}
        </button>
      </div>
    </div>

    <!-- Load / API error (data is fetched once; errors are visible and retryable) -->
    <div v-if="error" class="api-error-banner" role="alert">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <span>{{ error }}</span>
      <button type="button" class="btn-retry" @click="loadProducts">Retry</button>
    </div>

    <div class="content-wrapper">

      <!-- CATEGORY FILTER -->
      <aside class="side-panel category-panel" aria-label="Filter by category">
        <div class="category-panel__header">
          <div class="category-panel__intro">
            <span class="category-panel__badge" aria-hidden="true">
              <i class="fa-solid fa-layer-group"></i>
            </span>
            <div>
              <h3 class="category-panel__title">Categories</h3>
              <p class="category-panel__subtitle">Filter by sport</p>
            </div>
          </div>
          <button
            v-if="selectedCategory"
            type="button"
            class="category-panel__clear"
            @click="clearCategory"
          >
            <i class="fa-solid fa-rotate-left"></i>
            Reset
          </button>
        </div>

        <div class="category-grid" role="list">
          <button
            v-for="cat in categoryFilters"
            :key="cat.name"
            type="button"
            class="category-chip"
            :class="{ active: selectedCategory === cat.name }"
            role="listitem"
            :aria-pressed="selectedCategory === cat.name"
            @click="selectCategory(cat.name)"
          >
            <span class="category-chip__icon" aria-hidden="true">
              <i :class="cat.icon"></i>
            </span>
            <span class="category-chip__body">
              <span class="category-chip__label">{{ cat.name }}</span>
              <span class="category-chip__count">{{ getCategoryCount(cat.name) }} listings</span>
            </span>
            <i
              class="fa-solid fa-check category-chip__check"
              aria-hidden="true"
              v-if="selectedCategory === cat.name"
            ></i>
          </button>
        </div>
      </aside>

      <!-- PRODUCT GRID -->
      <div class="product-grid-container">
        <div class="section-header">
          <h2 class="section-title">
            <span class="section-title__icon" aria-hidden="true">
              <i :class="getSectionIcon()"></i>
            </span>
            {{ getSectionTitle() }}
          </h2>
          <div class="sort-options">
            <label class="sort-label" for="product-sort">Sort</label>
            <select id="product-sort" v-model="sortOption">
              <option value="recent">Most Recent</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Best Rated</option>
            </select>
          </div>
        </div>

        <div class="product-grid">
          <div
            class="product-card"
            v-for="(item, index) in displayedProducts"
            :key="item.listing_id"
          >

            <!-- IMAGE SLIDER - ENHANCED -->
            <div class="image-slider" @click="toggleCardDetails(item)">
              <!-- CONDITION TAG OVERLAY -->
              <div class="condition-badge" :class="getConditionClass(item.condition)">
                <i class="fa-solid fa-medal"></i>
                <span>{{ item.condition }}</span>
              </div>

              <!-- MAIN IMAGE WITH ERROR HANDLING -->
              <div v-if="item.images && item.images.length > 0" class="image-container">
                <img 
                  :src="displayProductImageSrc(item, 'main')"
                  class="main-img"
                  :loading="index < 8 ? 'eager' : 'lazy'"
                  decoding="async"
                  @error="handleImageError(item, 'main')"
                  :alt="item.title || 'Product image'"
                />
                
                <!-- IMAGE COUNTER -->
                <div v-if="item.images.length > 1" class="image-counter">
                  <i class="fa-solid fa-images"></i> {{ item.activeImage + 1 }}/{{ item.images.length }}
                </div>
              </div>
              
              <!-- FALLBACK IF NO IMAGES -->
              <div v-else class="no-image">
                <i class="fa-solid fa-image"></i>
                <p>No Image Available</p>
              </div>
            </div>

            <!-- COMPACT DETAILS (ALWAYS VISIBLE) -->
            <div class="compact-details">
              <h3 class="product-title">{{ item.title }}</h3>
              
              <div class="brand-badge">
                <i class="fa-solid fa-tag"></i>
                <span>{{ item.brand }}</span>
              </div>
              
              <div class="price-seller-row">
                <div class="price-container">
                  <h2 class="price">KES {{ formatPrice(item.price) }}</h2>
                  <span v-if="item.negotiable" class="negotiable-badge">Negotiable</span>
                </div>
                <div class="seller-mini">
                  <img 
                    :src="sellerAvatarSrc(item)"
                    loading="lazy"
                    decoding="async"
                    @error="handleSellerImageError(item)"
                    alt=""
                    class="seller-avatar-mini"
                  />
                  <div class="seller-info-mini">
                    <span class="seller-name-mini">{{ item.seller.name }}</span>
                    <div class="seller-rating-mini">
                      <i class="fa-solid fa-star"></i>
                      <span>{{ formatRating(item.seller.rating) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="meta-compact">
                <span class="meta-item">
                  <i class="fa-regular fa-clock"></i> {{ item.age }}
                </span>
                <span class="meta-item">
                  <i class="fa-solid fa-location-dot"></i> {{ item.location }}
                </span>
              </div>
            </div>

            <!-- EXPANDABLE DETAILS PANEL -->
            <transition name="slide-down">
              <div v-if="expandedListingId === item.listing_id" class="expanded-details">
                
                <!-- THUMBNAILS -->
                <div v-if="item.images && item.images.length > 1" class="thumbs">
                  <div 
                    v-for="(thumb, tIndex) in item.images" 
                    :key="tIndex"
                    class="thumbnail-container"
                    :class="{ active: item.activeImage === tIndex }"
                    @click.stop="setActiveImage(item, tIndex)"
                  >
                    <img
                      :src="displayProductImageSrc(item, 'thumb', tIndex)"
                      loading="lazy"
                      decoding="async"
                      @error="handleImageError(item, 'thumb', tIndex)"
                      :alt="`Thumbnail ${tIndex + 1}`"
                    />
                  </div>
                </div>

                <!-- FULL DETAILS -->
                <div class="full-details">
                  <div class="meta-full">
                    <div class="meta-item-full">
                      <i class="fa-solid fa-tags"></i>
                      <span>{{ item.category }}</span>
                    </div>
                    <div class="meta-item-full" v-if="item.negotiable">
                      <i class="fa-solid fa-handshake"></i>
                      <span>Negotiable Price</span>
                    </div>
                  </div>
                  
                  <!-- SELLER INFO -->
                  <div class="seller-card-expanded">
                    <div class="seller-avatar">
                      <img 
                        :src="sellerAvatarSrc(item)"
                        loading="lazy"
                        decoding="async"
                        @error="handleSellerImageError(item)"
                        alt=""
                      />
                      <div class="verified-badge" v-if="item.seller.verified">
                        <i class="fa-solid fa-circle-check"></i>
                      </div>
                    </div>
                    <div class="seller-info-expanded">
                      <h4 class="seller-name">{{ item.seller.name }}</h4>
                      <div class="seller-stats">
                        <span class="rating">
                          <i class="fa-solid fa-star"></i>
                          {{ formatRating(item.seller.rating) }}
                        </span>
                        <span class="reviews">
                          <i class="fa-solid fa-comment"></i>
                          {{ item.seller.reviews }} reviews
                        </span>
                      </div>
                      <p class="seller-extra-meta">
                        <span>
                          <i class="fa-solid fa-store"></i>
                          {{ item.seller.active_listings_count }} active listing{{
                            item.seller.active_listings_count === 1 ? "" : "s"
                          }}
                        </span>
                        <span>
                          <i class="fa-solid fa-calendar"></i>
                          Member since {{ formatMemberSince(item.seller.member_since) }}
                        </span>
                      </p>
                    </div>
                  </div>

                  <!-- DESCRIPTION -->
                  <div v-if="item.short_description" class="description">
                    <h4><i class="fa-solid fa-align-left"></i> Description</h4>
                    <p>{{ item.short_description }}</p>
                  </div>

                  <!-- ACTION BUTTONS -->
                  <div class="actions">
                    <button class="btn-view-details" @click.stop="openProduct(item.listing_id)">
                      <i class="fa-solid fa-eye"></i>
                      View Details
                    </button>
                    <button class="btn-request" @click.stop="openProduct(item.listing_id)">
                      <i class="fa-solid fa-shopping-cart"></i>
                      Request to Buy
                    </button>
                  </div>

                  <div class="safety-tip">
                    <i class="fa-solid fa-shield-alt"></i>
                    <span>Always meet in safe public places for exchanges</span>
                  </div>
                </div>
              </div>
            </transition>

            <!-- EXPAND/COLLAPSE INDICATOR -->
            <div class="expand-indicator" @click="toggleCardDetails(item)">
              <i :class="expandedListingId === item.listing_id ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
              <span>{{ expandedListingId === item.listing_id ? 'Show Less' : 'View Details' }}</span>
            </div>
          </div>
        </div>

        <!-- LOADING STATE -->
        <transition name="fade">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading products...</p>
          </div>
        </transition>

        <!-- EMPTY STATE -->
        <transition name="fade">
          <div v-if="!loading && displayedProducts.length === 0" class="empty-state">
            <div class="empty-state-icon">
              <i class="fa-solid fa-box-open"></i>
            </div>
            <h3>No products found</h3>
            <p v-if="searchQuery">Try searching with different keywords</p>
            <p v-else>Try selecting a different category</p>
            <button class="btn-clear-filters" @click="clearFilters">
              <i class="fa-solid fa-undo"></i>
              Clear All Filters
            </button>
          </div>
        </transition>

        <!-- RESULTS COUNT -->
        <div class="results-count" v-if="displayedProducts.length > 0">
          <span>{{ displayedProducts.length }} products found</span>
        </div>

        <!-- INVITING MARKETPLACE FEATURES SECTION -->
        <div class="marketplace-features">
          <h2 class="features-title">
            <i class="fa-solid fa-store"></i>
            Why Buy From Our Marketplace?
          </h2>
          
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-shield-alt"></i>
              </div>
              <h3>Safe Transactions</h3>
              <p>All sellers are verified and we promote safe meetup locations for exchanges.</p>
            </div>
            
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-handshake"></i>
              </div>
              <h3>Price Negotiation</h3>
              <p>Most items have negotiable prices. Chat directly with sellers to get the best deal.</p>
            </div>
            
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-search"></i>
              </div>
              <h3>Quality Assurance</h3>
              <p>Detailed condition descriptions and multiple photos for each product.</p>
            </div>
            
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-star"></i>
              </div>
              <h3>Verified Sellers</h3>
              <p>Seller ratings and reviews help you make informed buying decisions.</p>
            </div>
            
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-tags"></i>
              </div>
              <h3>Great Value</h3>
              <p>Save up to 70% compared to new items. Premium brands at affordable prices.</p>
            </div>
            
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-headset"></i>
              </div>
              <h3>Easy Communication</h3>
              <p>Direct messaging system to ask questions and arrange meetups.</p>
              <p class="feature-coming-soon">Feature coming soon</p>
            </div>
          </div>
          
          <div class="cta-section">
            <div class="cta-content">
              <h3>Ready to find your perfect sports gear?</h3>
              <p>Join thousands of satisfied buyers in our trusted marketplace.</p>
              <div class="cta-stats">
                <div class="stat">
                  <i class="fa-solid fa-users"></i>
                  <span>{{ activeBuyersStatText }}</span>
                </div>
                <div class="stat">
                  <i class="fa-solid fa-thumbs-up"></i>
                  <span>95% Satisfaction Rate</span>
                </div>
                <div class="stat">
                  <i class="fa-solid fa-bolt"></i>
                  <span>Fast Responses</span>
                </div>
              </div>
            </div>
            <button class="btn-cta" @click="scrollToTop">
              <i class="fa-solid fa-arrow-up"></i>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import Footer from "./Footer.vue";
import { buildApiUrl, API_BASE_URL } from "@/config/api";

/** SVG placeholders (no network); avoid mutating failed URLs in-place (prevents retry loops). */
const PLACEHOLDER_PRODUCT =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg";
const PLACEHOLDER_PRODUCT_ERR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWVlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIHVuYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg";
const PLACEHOLDER_SELLER =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjUiIGN5PSIyNSIgcj0iMjUiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSIyNSIgeT0iMjgiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+UzwvdGV4dD48L3N2Zz4";

/** Category labels + icons aligned with main store / CategorySlide */
const CATEGORY_FILTERS = [
  { name: "Football", icon: "fa-solid fa-futbol" },
  { name: "Basketball", icon: "fa-solid fa-basketball" },
  { name: "Gym & Fitness", icon: "fa-solid fa-dumbbell" },
  { name: "Swimming", icon: "fa-solid fa-person-swimming" },
  { name: "Cycling", icon: "fa-solid fa-bicycle" },
  { name: "Outdoor & Hiking", icon: "fa-solid fa-mountain" },
];

export default {
  name: "BuyerProductGrid",
  components: { MainNavbar, Footer },

  data() {
    return {
      selectedCategory: "Football",
      categoryFilters: CATEGORY_FILTERS,
      products: [],
      loading: false,
      error: null,
      expandedListingId: null,
      searchQuery: "",
      sortOption: "recent",
      popularBrands: ["Nike", "Adidas", "Puma", "Under Armour", "Reebok"],
      searchResults: [],
      isSearching: false,
      /** Keys: `${listingId}-main`, `${listingId}-thumb-${i}` */
      imageErrorMap: {},
      /** seller_id or listing_id fallback */
      sellerImageErrorMap: {},
      loadAbortController: null,
      /** From GET /seller/public/seller-count; null = not loaded or failed */
      publicSellerCount: null,
    };
  },

  computed: {
    /**
     * Marketing line uses seller-account count from API; if count is under 10 or unavailable, show 15+.
     */
    activeBuyersStatText() {
      const n = this.publicSellerCount;
      if (n === null || typeof n !== "number" || n < 10) {
        return "15+ Active Buyers";
      }
      const formatted = new Intl.NumberFormat("en-KE").format(n);
      return `${formatted}+ Active Buyers`;
    },

    filteredProducts() {
      if (!this.selectedCategory && !this.searchQuery) return this.products;

      let filtered = this.products;

      if (this.selectedCategory) {
        filtered = filtered.filter((item) => item.category === this.selectedCategory);
      }

      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            item.brand.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query)
        );
      }

      return filtered;
    },

    /** Unsorted list: search mode uses searchResults; otherwise category + text filter. */
    displayedProductsUnsorted() {
      if (this.isSearching && this.searchQuery && this.searchQuery.trim() !== "") {
        return this.searchResults;
      }
      return this.filteredProducts;
    },

    /**
     * Sorted copy — does not mutate `products` (sorting used to replace the full catalog with
     * only the filtered slice and broke category switching).
     */
    displayedProducts() {
      const list = [...this.displayedProductsUnsorted];
      const opt = this.sortOption;
      switch (opt) {
        case "price-low":
          list.sort((a, b) => (a.price || 0) - (b.price || 0));
          break;
        case "price-high":
          list.sort((a, b) => (b.price || 0) - (a.price || 0));
          break;
        case "rating":
          list.sort(
            (a, b) => (b.seller?.rating || 0) - (a.seller?.rating || 0)
          );
          break;
        case "recent":
        default:
          list.sort((a, b) => {
            const ta = a.listing_created_at
              ? new Date(a.listing_created_at).getTime()
              : 0;
            const tb = b.listing_created_at
              ? new Date(b.listing_created_at).getTime()
              : 0;
            if (tb !== ta) return tb - ta;
            return (b.listing_id || 0) - (a.listing_id || 0);
          });
          break;
      }
      return list;
    },
  },

  mounted() {
    this.loadProducts();
    this.loadPublicSellerCount();
  },

  beforeUnmount() {
    if (this.loadAbortController) {
      this.loadAbortController.abort();
    }
  },

  methods: {
    async loadPublicSellerCount() {
      try {
        const res = await fetch(buildApiUrl("/seller/public/seller-count"), {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          this.publicSellerCount = null;
          return;
        }
        const data = await res.json();
        const raw = data?.seller_count;
        const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
        this.publicSellerCount = Number.isFinite(n) && n >= 0 ? n : null;
      } catch {
        this.publicSellerCount = null;
      }
    },

    async loadProducts() {
      if (this.loadAbortController) {
        this.loadAbortController.abort();
      }
      this.loadAbortController = new AbortController();
      const { signal } = this.loadAbortController;

      this.loading = true;
      this.error = null;

      try {
        const res = await fetch(buildApiUrl("/seller/listings/all_listings"), {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal,
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch listings: ${res.status}`);
        }

        const data = await res.json();

        // Transform backend structure into UI-friendly format
        this.products = data.map((item) => ({
          listing_id: item.listing_id,
          title: item.title,
          brand: item.brand,
          age: item.age,
          category: item.category,
          condition: item.condition,
          price: item.price,
          negotiable: item.negotiable,
          location: item.location,
          description: item.short_description || "",
          short_description: item.short_description,
          full_description: item.full_description,
          listing_created_at: item.listing_created_at || null,
          images: Array.isArray(item.images) ? item.images : [],
          activeImage: 0,

          seller: {
            seller_id: item.seller?.seller_id ?? null,
            name: item.seller?.name || "Unknown Seller",
            image: item.seller?.picture || "",
            rating: item.seller?.rating ?? 0,
            reviews: item.seller?.reviews ?? 0,
            verified: item.seller?.verified || false,
            active_listings_count: item.seller?.active_listings_count ?? 0,
            member_since: item.seller?.member_since || null,
          },
        }));
        this.imageErrorMap = {};
        this.sellerImageErrorMap = {};
        this.expandedListingId = null;
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        console.error("Error loading products:", err);
        this.error =
          err.message || "Could not load listings. Check your connection and try again.";
      } finally {
        this.loading = false;
      }
    },
    
    performSearch() {
      if (!this.searchQuery.trim()) {
        this.isSearching = false;
        this.searchResults = [];
        return;
      }
      
      this.isSearching = true;
      const query = this.searchQuery.toLowerCase().trim();
      
      this.searchResults = this.products.filter(item => 
        item.title.toLowerCase().includes(query) ||
        item.brand.toLowerCase().includes(query) ||
        (item.description && item.description.toLowerCase().includes(query))
      );
    },
    
    handleSearchInput() {
      if (!this.searchQuery.trim()) {
        this.isSearching = false;
        this.searchResults = [];
      }
    },
    
    searchByBrand(brand) {
      this.searchQuery = brand;
      this.performSearch();
    },
    
    clearSearch() {
      this.searchQuery = "";
      this.isSearching = false;
      this.searchResults = [];
    },
    
    clearCategory() {
      this.selectedCategory = null;
    },

    selectCategory(name) {
      this.selectedCategory = name;
      this.searchQuery = "";
      this.isSearching = false;
      this.searchResults = [];
      this.expandedListingId = null;
    },

    getCategoryIcon(categoryName) {
      const match = this.categoryFilters.find((c) => c.name === categoryName);
      return match?.icon || "fa-solid fa-tag";
    },

    getSectionIcon() {
      if (this.searchQuery && this.isSearching) {
        return "fa-solid fa-magnifying-glass";
      }
      if (this.selectedCategory) {
        return this.getCategoryIcon(this.selectedCategory);
      }
      return "fa-solid fa-store";
    },

    clearFilters() {
      this.selectedCategory = null;
      this.searchQuery = "";
      this.isSearching = false;
      this.searchResults = [];
      this.sortOption = "recent";
      this.expandedListingId = null;
    },

    getCategoryCount(category) {
      return this.products.filter(item => item.category === category).length;
    },
    
    getSectionTitle() {
      if (this.searchQuery && this.isSearching) {
        return `Search Results for "${this.searchQuery}"`;
      } else if (this.selectedCategory) {
        return `${this.selectedCategory} Gear`;
      }
      return "All Sports Gear";
    },
    
    getConditionClass(condition) {
      const conditions = {
        'New': 'condition-new',
        'Like New': 'condition-like-new',
        'Good': 'condition-good',
        'Fair': 'condition-fair',
        'Used': 'condition-used'
      };
      return conditions[condition] || 'condition-used';
    },
    
    formatPrice(price) {
      return new Intl.NumberFormat().format(price);
    },

    formatRating(rating) {
      const n = typeof rating === "number" ? rating : parseFloat(rating);
      if (Number.isNaN(n)) return "0";
      return n.toFixed(1);
    },

    formatMemberSince(iso) {
      if (!iso) return "recently";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "recently";
        return d.toLocaleDateString("en-KE", { month: "short", year: "numeric" });
      } catch {
        return "recently";
      }
    },

    openProduct(id) {
      this.$router.push(`/ipromiseafrica/resale/product/${id}`);
    },

    toggleCardDetails(item) {
      const id = item.listing_id;
      this.expandedListingId = this.expandedListingId === id ? null : id;
    },

    /** Let another photo load after a main-image failure. */
    setActiveImage(item, tIndex) {
      item.activeImage = tIndex;
      const mainKey = `${item.listing_id}-main`;
      if (this.imageErrorMap[mainKey]) {
        const next = { ...this.imageErrorMap };
        delete next[mainKey];
        this.imageErrorMap = next;
      }
    },
    
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    /** Resolve relative image paths against API base (same host as API). */
    getImageUrl(url) {
      if (!url) {
        return PLACEHOLDER_PRODUCT;
      }
      if (url.startsWith("data:")) {
        return url;
      }
      if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
      }
      const path = url.startsWith("/") ? url : `/${url.replace(/^\.\//, "")}`;
      return `${API_BASE_URL}${path}`;
    },

    displayProductImageSrc(item, role, thumbIndex = null) {
      const lid = item.listing_id;
      if (role === "main") {
        const key = `${lid}-main`;
        if (this.imageErrorMap[key]) return PLACEHOLDER_PRODUCT_ERR;
        const raw = item.images?.[item.activeImage];
        return this.getImageUrl(raw);
      }
      const key = `${lid}-thumb-${thumbIndex}`;
      if (this.imageErrorMap[key]) return PLACEHOLDER_PRODUCT_ERR;
      return this.getImageUrl(item.images?.[thumbIndex]);
    },

    sellerAvatarSrc(item) {
      const sid = item.seller?.seller_id ?? item.listing_id;
      if (this.sellerImageErrorMap[sid]) return PLACEHOLDER_SELLER;
      return this.getImageUrl(item.seller?.image);
    },

    handleImageError(item, type, index = null) {
      const lid = item.listing_id;
      const key =
        type === "main" ? `${lid}-main` : `${lid}-thumb-${index}`;
      this.imageErrorMap[key] = true;
    },

    handleSellerImageError(item) {
      const sid = item.seller?.seller_id ?? item.listing_id;
      this.sellerImageErrorMap[sid] = true;
    },
  },
};
</script>

<style scoped>
/* GLOBAL LAYOUT */
.buyer-layout {
  background: var(--background-color1);
  color: var(--text-color1);
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
  padding-top: 80px;
  box-sizing: border-box;
}

/* API error */
.api-error-banner {
  width: 100%;
  max-width: none;
  margin: 0 0 16px;
  padding: 12px clamp(1rem, 3vw, 2rem);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--pending-bg, rgba(220, 38, 38, 0.08));
  border: 1px solid var(--pending-border, rgba(220, 38, 38, 0.35));
  border-radius: 12px;
  color: var(--text-color1);
}

.api-error-banner i {
  color: var(--pending-color, #b91c1c);
}

.btn-retry {
  margin-left: auto;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color-hover);
  background: var(--border-color-hover);
  color: var(--text-color3);
  font-weight: 600;
  cursor: pointer;
}

.btn-retry:hover {
  opacity: 0.92;
}

.sort-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.sort-options {
  position: relative;
}

/* ENHANCED SEARCH CONTAINER */
.search-container {
  width: 100%;
  max-width: none;
  margin: 0 0 30px;
  padding: 0 clamp(1rem, 3vw, 2rem);
  box-sizing: border-box;
}

.search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  background: var(--background-color2);
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--box-shadow);
  border: 1px solid var(--border-color-hover1);
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 15px;
  color: var(--subtext-color);
  font-size: 16px;
}

.search-input-wrapper input {
  width: 100%;
  padding: 12px 45px 12px 45px;
  border-radius: 8px;
  border: 2px solid var(--border-color-hover1);
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 16px;
  transition: all 0.3s ease;
}

.search-input-wrapper input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
}

.clear-search {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--subtext-color);
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.clear-search:hover {
  background: var(--background-color1);
  color: var(--text-color1);
}

.search-btn {
  padding: 12px 24px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.search-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px var(--box-shadow);
}

.search-tips {
  margin-bottom: 15px;
  padding: 10px 15px;
  background: var(--action-info-bg);
  border-radius: 8px;
  border-left: 4px solid var(--border-color-hover);
}

.search-tips p {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--text-color1);
  font-size: 14px;
}

.search-tips i {
  color: var(--border-color-hover);
}

.quick-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.quick-filter {
  padding: 8px 16px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: 20px;
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.quick-filter:hover {
  background: var(--border-color-hover1);
  transform: translateY(-2px);
}

/* MAIN WRAPPER */
.content-wrapper {
  display: flex;
  gap: clamp(1.25rem, 2.5vw, 2rem);
  padding: 0 clamp(1rem, 3vw, 2rem) clamp(2rem, 4vw, 3rem);
  width: 100%;
  max-width: none;
  margin: 0;
  align-items: flex-start;
  box-sizing: border-box;
}

/* CATEGORY PANEL */
.side-panel.category-panel {
  width: min(100%, 320px);
  flex-shrink: 0;
  background: linear-gradient(
    165deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--background-color1) 88%, var(--background-color2)) 100%
  );
  border-radius: 20px;
  padding: clamp(1.1rem, 2vw, 1.35rem);
  box-shadow:
    0 8px 32px color-mix(in srgb, var(--background-color2) 55%, transparent),
    0 0 0 1px color-mix(in srgb, var(--border-color-hover) 18%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
  position: sticky;
  top: 96px;
  max-height: calc(100vh - 110px);
  max-height: calc(100dvh - 110px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.category-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1.1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
}

.category-panel__intro {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.category-panel__badge {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  color: var(--border-color-hover);
  flex-shrink: 0;
  font-size: 1.05rem;
}

.category-panel__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-color1);
  letter-spacing: 0.02em;
}

.category-panel__subtitle {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: var(--text-color2);
}

.category-panel__clear {
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 35%, transparent);
  background: color-mix(in srgb, var(--border-color-hover) 8%, transparent);
  color: var(--border-color-hover);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.category-panel__clear:hover {
  background: color-mix(in srgb, var(--border-color-hover) 16%, transparent);
  transform: translateY(-1px);
}

.category-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 2px;
  scrollbar-width: thin;
}

.category-chip {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
  background: color-mix(in srgb, var(--background-color1) 65%, transparent);
  color: var(--text-color1);
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s ease;
  font-family: inherit;
}

.category-chip:hover {
  border-color: color-mix(in srgb, var(--border-color-hover) 40%, transparent);
  background: color-mix(in srgb, var(--border-color-hover) 6%, var(--background-color1));
  transform: translateX(3px);
}

.category-chip.active {
  border-color: var(--border-color-hover);
  background: color-mix(in srgb, var(--border-color-hover) 12%, var(--background-color1));
  box-shadow: 0 6px 20px color-mix(in srgb, var(--border-color-hover) 18%, transparent);
}

.category-chip__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  color: var(--border-color-hover);
  font-size: 1.05rem;
  flex-shrink: 0;
  transition: background 0.25s ease, color 0.25s ease;
}

.category-chip.active .category-chip__icon {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.category-chip__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.category-chip__label {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text-color1);
  line-height: 1.3;
}

.category-chip__count {
  font-size: 0.72rem;
  color: var(--text-color2);
}

.category-chip__check {
  color: var(--border-color-hover);
  font-size: 0.85rem;
  flex-shrink: 0;
}

/* PRODUCT GRID CONTAINER */
.product-grid-container {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--border-color-hover1);
}

.section-title {
  font-size: clamp(1.25rem, 2vw, 1.6rem);
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
}

.section-title__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  color: var(--border-color-hover);
  font-size: 1.15rem;
  flex-shrink: 0;
}

.section-title__icon i {
  color: inherit;
}

/* ENHANCED PRODUCT GRID */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: clamp(1rem, 2vw, 1.75rem);
  margin-bottom: 40px;
  width: 100%;
}

.sort-options select {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  min-width: 180px;
}

.sort-options select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

/* ENHANCED PRODUCT CARD */
.product-card {
  background: var(--background-color2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px var(--box-shadow);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  border: 1px solid var(--border-color-hover1);
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px var(--box-shadow);
  border-color: var(--border-color-hover);
}

/* IMAGE SLIDER ENHANCEMENTS */
.image-slider {
  position: relative;
  height: 220px;
  overflow: hidden;
}

.image-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.main-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--background-color1);
}

.product-card:hover .main-img {
  transform: scale(1.1);
}

/* CONDITION BADGE */
.condition-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 10;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.condition-new {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9));
  color: white;
}

.condition-like-new {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.9));
  color: white;
}

.condition-good {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.9), rgba(217, 119, 6, 0.9));
  color: white;
}

.condition-fair {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.9), rgba(245, 158, 11, 0.9));
  color: white;
}

.condition-used {
  background: linear-gradient(135deg, rgba(148, 163, 184, 0.9), rgba(100, 116, 139, 0.9));
  color: white;
}

.image-counter {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  backdrop-filter: blur(10px);
}

/* COMPACT DETAILS ENHANCEMENTS */
.compact-details {
  padding: 16px;
}

.product-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 44px;
}

.brand-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--background-color1);
  color: var(--text-color2);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  margin-bottom: 12px;
  border: 1px solid var(--border-color-hover1);
}

.price-seller-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.price-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price {
  font-size: 20px;
  font-weight: 700;
  color: var(--border-color-hover);
  line-height: 1;
}

.negotiable-badge {
  background: var(--action-success-bg);
  color: var(--action-success-color);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  width: fit-content;
  border: 1px solid var(--action-success-color);
}

.seller-mini {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seller-avatar-mini {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color-hover1);
}

.seller-info-mini {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.seller-name-mini {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-color1);
}

.seller-rating-mini {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--pending-color);
}

.meta-compact {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-color2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* EXPANDABLE DETAILS ENHANCEMENTS */
.expanded-details {
  padding: 16px;
  border-top: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
}

.slide-down-enter-active {
  animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-leave-active {
  animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1) reverse;
}

@keyframes slideDown {
  from {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    max-height: 500px;
    opacity: 1;
    padding-top: 16px;
    padding-bottom: 16px;
  }
}

.thumbs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.thumbnail-container {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.thumbnail-container:hover {
  border-color: var(--border-color-hover1);
  transform: translateY(-2px);
}

.thumbnail-container.active {
  border-color: var(--border-color-hover);
  box-shadow: 0 4px 12px rgba(var(--border-color-hover-rgb), 0.2);
}

.thumbnail-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.full-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.meta-full {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-item-full {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--background-color2);
  border-radius: 12px;
  font-size: 12px;
  color: var(--text-color2);
  border: 1px solid var(--border-color-hover1);
}

.seller-card-expanded {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--background-color2);
  border-radius: 12px;
  border: 1px solid var(--border-color-hover1);
}

.seller-avatar {
  position: relative;
}

.seller-avatar img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color-hover1);
}

.verified-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: var(--approved-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--background-color2);
}

.verified-badge i {
  color: white;
  font-size: 8px;
}

.seller-info-expanded {
  flex: 1;
}

.seller-name {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--text-color1);
}

.seller-stats {
  display: flex;
  gap: 12px;
}

.seller-extra-meta {
  margin: 10px 0 0 0;
  font-size: 0.82rem;
  color: var(--subtext-color, #94a3b8);
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
}

.seller-extra-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.seller-stats span {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-color2);
}

.description h4 {
  font-size: 14px;
  margin-bottom: 8px;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 8px;
}

.description p {
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-color2);
}

.actions {
  display: flex;
  gap: 8px;
}

.btn-view-details, .btn-request {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-view-details {
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-view-details:hover {
  background: rgba(var(--border-color-hover-rgb), 0.1);
}

.btn-request {
  background: var(--border-color-hover);
  border: 1px solid var(--border-color-hover);
  color: var(--text-color3);
}

.btn-request:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.safety-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: var(--pending-bg);
  border-radius: 8px;
  font-size: 12px;
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.expand-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--background-color1);
  border-top: 1px solid var(--border-color-hover1);
  color: var(--border-color-hover);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.expand-indicator:hover {
  background: var(--background-color2);
}

.expand-indicator i {
  transition: transform 0.3s ease;
}

/* RESULTS COUNT */
.results-count {
  text-align: center;
  padding: 15px;
  color: var(--text-color2);
  font-size: 14px;
  border-top: 1px solid var(--border-color-hover1);
  margin-bottom: 40px;
}

/* MARKETPLACE FEATURES SECTION */
.marketplace-features {
  margin-top: 60px;
  padding: 40px;
  background: var(--background-color2);
  border-radius: 20px;
  box-shadow: 0 8px 32px var(--box-shadow);
  border: 1px solid var(--border-color-hover1);
}

.features-title {
  text-align: center;
  font-size: 28px;
  margin-bottom: 40px;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
}

.feature-card {
  background: var(--background-color1);
  padding: 30px;
  border-radius: 16px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--border-color-hover1);
}

.feature-card:hover {
  transform: translateY(-10px);
  border-color: var(--border-color-hover);
  box-shadow: 0 12px 40px var(--box-shadow);
}

.feature-icon {
  width: 70px;
  height: 70px;
  background: var(--gradient);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 28px;
  color: var(--text-color3);
}

.feature-card h3 {
  font-size: 20px;
  margin-bottom: 15px;
  color: var(--text-color1);
}

.feature-card p {
  color: var(--text-color2);
  line-height: 1.6;
  font-size: 15px;
}

.feature-coming-soon {
  margin-top: 14px;
  margin-bottom: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--border-color-hover);
  letter-spacing: 0.02em;
}

.cta-section {
  text-align: center;
  padding: 40px;
  background: var(--background-color1);
  border-radius: 16px;
  border: 2px solid var(--border-color-hover);
}

.cta-content h3 {
  font-size: 24px;
  margin-bottom: 10px;
  color: var(--text-color1);
}

.cta-content p {
  color: var(--text-color2);
  margin-bottom: 30px;
  font-size: 16px;
}

.cta-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-color1);
}

.stat i {
  color: var(--border-color-hover);
  font-size: 18px;
}

.btn-cta {
  padding: 16px 40px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
}

.btn-cta:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(var(--border-color-hover-rgb), 0.3);
}

/* LOADING STATE */
.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--loader-border);
  border-top-color: var(--loader-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-state p {
  color: var(--text-color1);
  font-size: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-state-icon {
  width: 80px;
  height: 80px;
  background: var(--background-color2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 36px;
  color: var(--text-color2);
}

.empty-state h3 {
  font-size: 24px;
  margin-bottom: 10px;
  color: var(--text-color1);
}

.empty-state p {
  color: var(--text-color2);
  margin-bottom: 30px;
  font-size: 16px;
}

.btn-clear-filters {
  padding: 12px 24px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: 8px;
  color: var(--text-color1);
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-clear-filters:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

/* RESPONSIVE DESIGN */
@media (max-width: 1200px) {
  .content-wrapper {
    flex-direction: column;
  }

  .side-panel.category-panel {
    width: 100%;
    position: static;
    max-height: none;
    overflow: visible;
  }

  .category-grid {
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 12px;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .category-chip {
    flex: 0 0 auto;
    width: min(260px, 78vw);
    min-height: 72px;
  }

  .category-chip:hover {
    transform: translateY(-2px);
  }

  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .buyer-layout {
    padding-top: 70px;
  }
  
  .search-container {
    padding: 0 clamp(0.85rem, 3vw, 1.25rem);
  }
  
  .search-bar {
    flex-direction: column;
  }

  .search-btn span {
    display: none;
  }

  .search-btn {
    justify-content: center;
    min-width: 48px;
  }
  
  .content-wrapper {
    padding: 0 clamp(0.85rem, 3vw, 1.25rem) clamp(1.75rem, 4vw, 2.5rem);
  }

  .category-panel__header {
    margin-bottom: 0.85rem;
    padding-bottom: 0.85rem;
  }

  .category-chip {
    width: min(240px, 82vw);
    padding: 10px 12px;
  }

  .category-chip__icon {
    width: 36px;
    height: 36px;
    font-size: 0.95rem;
  }

  .section-title__icon {
    width: 38px;
    height: 38px;
    font-size: 1rem;
  }
  
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: 16px;
  }
  
  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .cta-stats {
    flex-direction: column;
    gap: 20px;
  }
  
  .marketplace-features {
    padding: 30px 20px;
  }
}

@media (max-width: 480px) {
  .product-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: clamp(1.1rem, 4vw, 1.35rem);
  }

  .image-slider {
    height: min(52vw, 240px);
  }
  
  .price-seller-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .quick-filters {
    justify-content: flex-start;
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;
  }

  .quick-filter {
    flex: 0 0 auto;
  }
}

/* FADE ANIMATIONS */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.35s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}
</style>